<template>
  <div>
    <div v-if="parentCompany">
      <v-layout wrap justify-center mt-12>
        <v-flex xs12 sm9>
          <v-layout wrap>
            <v-flex xs12>
              <h1>{{ $t('a') }} - {{ parentCompany.name }}</h1>
            </v-flex>
          </v-layout>

          <v-layout wrap mt-8>
            <v-flex xs12>
              <v-card class="px-8 pt-6 pb-6">
                <h2>{{ $t('b') }}</h2>
                <small>
                  <div v-if="['active', 'trialing'].includes(parentCompany.subscriptionStatus)">
                    {{ $t('3') }} {{ parentCompany.dossiersLeftNb }} {{ $t('4') }} {{ currentPeriodEnd }} {{ $t('4bis') }}
                  </div>
                  <div v-else>
                      <div class="orange--text" v-if="parentCompany.subscriptionStatus === 'canceled'">
                        <div><b>{{ $t('canceled1') }}</b></div>
                      </div>
                      <div class="orange--text" v-else>
                        <div><b>{{ $t('disabled1') }}</b></div>
                        <div>{{ $t('disabled2') }}</div>
                      </div>
                    </div>
                  <div class="text-center">
                    <div class="mt-4">
                      <v-btn to="/edit-subscription" outlined class="outlinedButtonWhiteBackground">{{ $t('d') }}</v-btn>
                    </div>
                    <div class="mt-4">
                      <v-layout v-if="isInitStripeBasicCustomerPortalSessionPending" wrap justify-center>
                        <v-flex xs4>
                          <v-progress-linear indeterminate color="primary"></v-progress-linear>
                        </v-flex>
                      </v-layout>
                      <a v-if="stripeBasicCustomerPortalUrl" :href="stripeBasicCustomerPortalUrl" target="_blank" outlined>{{ $t('d2') }}</a>
                    </div>
                  </div>
                </small>
              </v-card>
            </v-flex>
          </v-layout>

          <v-layout wrap mt-12 pt-8>
            <v-flex xs12>
              <v-card class="px-8 pt-6 pb-6">
                <h2>{{ $t('e') }}</h2>
                <v-layout wrap mt-3>
                  <v-flex xs12>
                    <small>
                      <div v-for="(childCompany, key) in parentCompany.childCompanies" :key="key">
                        <v-divider class="my-6" v-if="key !== 0"/>
                        <h3>{{ $t('f') }} {{ key + 1 }} - {{ childCompany.name }}</h3>
                        <div>
                          <div>
                            <span>{{ $t('g') }}</span>
                            <span v-if="childCompany.maxNbOfDossiers">{{ childCompany.maxNbOfDossiers }}</span>
                            <span v-else>{{ $t('h') }}</span>
                            <v-btn x-small fab @click="showEditChildCompanyMaxNbOfDossiers(childCompany)" :elevation="0" class="ml-2 mb-1">
                              <v-icon small>mdi-pen</v-icon>
                            </v-btn>
                          </div>
                          <div>
                            <span>{{ $t('i') }} {{ childCompany.dossiersLeftNb }}</span>
                            <span v-if="!childCompany.maxNbOfDossiers">{{ $t('j') }}</span>
                          </div>
                          <div class="mb-3">{{ $t('k') }} {{ childCompany.dossiersNbCreatedDuringCurrentBillingCycle }}</div>
                          <div>{{ $t('l') }} {{ childCompany.dossiersNbCreatedDuringLast30Days }}</div>
                        </div>
                      </div>
                    </small>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-flex>
          </v-layout>

          <v-layout wrap mt-12>
            <v-flex xs12>
              <h2>{{ $t('m') }}</h2>
              <v-layout wrap mt-3>
                <v-flex xs12 v-for="(childCompany, key) in parentCompany.childCompanies" :key="key" mb-12 pb-2>
                  <h4>
                    {{ $t('n') }} {{ key + 1 }} - {{ childCompany.name }}
                  </h4>
                  <div>
                    <small>
                      <div>
                        <b>{{ $t('o') }}</b> {{ childCompany.emailDomains.join(', ') }}.
                      </div>
                      <div>
                        <b>{{ $t('p') }}</b> {{ childCompany.templates.map(template => template.name).join(', ') }}.
                      </div>
                      <div>
                        {{ childCompany.companyUsers.length }} {{ $t('q') }}
                      </div>
                    </small>
                  </div>
                  <company-users-table
                    class="mt-2"
                    :company="childCompany"
                    :companyUsers="childCompany.companyUsers"
                    :isEditUserRoleEnabled="false"
                  />
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </div>
    <div v-else>
      <v-layout wrap justify-center>
        <v-flex xs6>
          <v-progress-linear indeterminate class="mt-12"/>
        </v-flex>
      </v-layout>
    </div>

    <v-dialog v-model="isShowingEditMaxNbOfDossiersOfChildCompany" width="40%" :persistent="isUpdatingMaxNbOfDossiersOfChildCompany">
      <v-card class="px-6 pb-6 pt-6">
        <div v-if="isUpdatingMaxNbOfDossiersOfChildCompany">
          <v-progress-linear indeterminate />
        </div>
        <div v-else-if="childCompanyOfEditMaxNbOfDossiers">
          <h3 class="text-center">{{ $t('r') }}</h3>
          <div class="mt-3">
            <small>
              <b>{{ $t('s') }} {{ childCompanyOfEditMaxNbOfDossiers.name }} {{ $t('t') }}</b>
            </small>
          </div>
          <div class="mt-3">
            <v-switch
              v-model="isMaxNbOfDossiersEnabled"
              color="secondary"
              inset
              :label="`${ $t('y2') } ${ isMaxNbOfDossiersEnabled ? $t('y3') : $t('y4') }`"
            />
            <div v-if="isMaxNbOfDossiersEnabled">
              <v-text-field v-model="newMaxNbOfDossiers" :label="$t('u')" type="number" :min="0" :step="1" outlined width="50"/>
            </div>
          </div>
          <v-layout wrap mt-3>
            <v-flex xs6 text-center>
              <v-btn class="primary" @click="updateMaxNbOfDossiersOfChildCompany" :disabled="isUpdateMaxNbOfDossiersOfChildCompanyDisabled">{{ $t('v') }}</v-btn>
            </v-flex>
            <v-flex xs6 text-center>
              <v-btn outlined @click="isShowingEditMaxNbOfDossiersOfChildCompany = false">{{ $t('w') }}</v-btn>
            </v-flex>
          </v-layout>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CompanyUsersTable from '@/components/CompanyUsersTable.vue';

export default {
  components: {
    CompanyUsersTable
  },
  data: () => ({
    parentCompany: null,
    stripeBasicCustomerPortalUrl: null,
    isInitStripeBasicCustomerPortalSessionPending: false,
    isShowingEditMaxNbOfDossiersOfChildCompany: false,
    childCompanyOfEditMaxNbOfDossiers: null,
    isUpdatingMaxNbOfDossiersOfChildCompany: false,
    isMaxNbOfDossiersEnabled: false,
    newMaxNbOfDossiers: null
  }),
  async mounted() {
    this.getParentCompanyAdminData()
    this.initStripeBasicCustomerPortalSession()
  },
  computed: {
    currentPeriodEnd() {
      if (!this.parentCompany) return null;
      return new Date(this.parentCompany.currentMonthlyPeriodDates.endDate * 1000).toLocaleDateString() //toLocaleString()
    },
    isUpdateMaxNbOfDossiersOfChildCompanyDisabled() {
      return this.isMaxNbOfDossiersEnabled && !((this.newMaxNbOfDossiers || this.newMaxNbOfDossiers === 0) && this.newMaxNbOfDossiers >= 0 && this.newMaxNbOfDossiers == parseInt(this.newMaxNbOfDossiers))
    }
  },
  methods: {
    async getParentCompanyAdminData() {
      this.parentCompany = (await this.$functions.httpsCallable('getParentCompanyAdminData')()).data.parentCompany
    },
    async initStripeBasicCustomerPortalSession() {
      this.isInitStripeBasicCustomerPortalSessionPending = true
      const stripeSessionReponse = await this.$functions.httpsCallable('createStripeCustomerPortalSession')({
        mode: 'basic',
        returnUrl: window.location.origin + '/account'
      })
      this.stripeBasicCustomerPortalUrl = stripeSessionReponse.data.url
      this.isInitStripeBasicCustomerPortalSessionPending = false
    },
    showEditChildCompanyMaxNbOfDossiers(childCompany) {
      this.childCompanyOfEditMaxNbOfDossiers = childCompany
      this.isMaxNbOfDossiersEnabled = childCompany.maxNbOfDossiers !== null
      this.newMaxNbOfDossiers = childCompany.maxNbOfDossiers
      this.isShowingEditMaxNbOfDossiersOfChildCompany = true
    },
    async updateMaxNbOfDossiersOfChildCompany() {
      this.isUpdatingMaxNbOfDossiersOfChildCompany = true
      await this.$functions.httpsCallable('updateMaxNbOfDossiersOfChildCompany')({
        maxNbOfDossiers: this.isMaxNbOfDossiersEnabled ? this.newMaxNbOfDossiers : null,
        childCompanyId: this.childCompanyOfEditMaxNbOfDossiers.id
      })
      this.$router.go()
    }
  }
}
</script>

<i18n>
{
  "fr": {
    "a": "Gestion du groupe",
    "b": "Abonnement du groupe",
    "c": "",
    "d": "Gérer votre abonnement",
    "d2": "Voir vos factures / Changer le mode de paiement",
    "disabled1": "Abonnement désactivé",
    "disabled2": "Le paiement de votre dernière facture n'a pas été réalisé.",
    "canceled1": "Abonnement annulé",
    "e": "Utilisation par équipe",
    "f": "Équipe",
    "g": "Nombre maximal de dossiers par mois : ",
    "h": "Pas de limite.",
    "i": "Nombre de dossiers restants pour ce cycle :",
    "j": " (commun au groupe).",
    "k": "Nombre de dossiers créés depuis le début du cycle mensuel actuel :",
    "l": "Nombre de dossiers créés sur les 30 derniers jours :",
    "m": "Liste des équipes",
    "n": "Équipe",
    "o": "Noms de domaines :",
    "p": "Templates :",
    "q": "utilisateurs.",
    "r": "Plafond de dossiers",
    "s": "Limiter le nombre maximal de dossiers que l'équipe",
    "t": "peut créer chaque mois.",
    "u": "Nombre maximal de dossiers mensuel",
    "v": "Mettre à jour",
    "w": "Annuler",

    "3": "Il reste",
    "4": "dossiers à votre groupe, jusqu'au",
    "4bis": "dans la journée.",

    "y2": "Limite",
    "y3": "activée",
    "y4": "désactivée"
  },
  "en": {

    "a": "Group management",
    "b": "Groupe subscription",
    "c": "",
    "d": "Manage subscription",
    "d2": "View invoices / Change payment method",
    "disabled1": "Subscription disabled",
    "disabled2": "The last payment has not been made.",
    "canceled1": "Subscription canceled",
    "e": "Teams usage",
    "f": "Team",
    "g": "Max number of CVs per month: ",
    "h": "No limit.",
    "i": "Number of CVs left for this cycle:",
    "j": " (shared with the group).",
    "k": "Number of CVs created since the beginning of current monthly cycle:",
    "l": "Number of CVs created over the last 30 days:",
    "m": "List of teams",
    "n": "Team",
    "o": "Domain names:",
    "p": "Templates:",
    "q": "users.",
    "r": "Team's usage limit",
    "s": "Limit the number of CVs that the team",
    "t": "can create each month.",
    "u": "Monthly max number of CVs",
    "v": "Update",
    "w": "Cancel",

    "3": "Your group has",
    "4": "CVs left, until the",
    "4bis": "by the end of the day.",

    
    "y2": "Limit",
    "y3": "enabled",
    "y4": "disabled"
  }
}